import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useQuery, useQueryClient, type QueryObserverResult } from '@tanstack/react-query'
import { useCallback } from 'react'
import { FullCreditApplication } from '../../types'
import { availableCreditKeys } from '../available-credit-api'
import { FullCreditApplicationDto } from './FullCreditApplicationDto'
import transformFullCreditApplicationFromApi from './full-credit-application-transform'

const SCOPE = 'fullCreditApplication'
const DETAIL = 'detail'

export const fullCreditApplicationKeys = {
  all: [SCOPE] as const,
  detail: (id: string, financingProgramId: EFinancingProgram) => [SCOPE, DETAIL, id, financingProgramId] as const,
}

const getFullCreditApplication = async (id: string, financingProgramId: EFinancingProgram) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<FullCreditApplicationDto>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(id)}/FullCreditApplication`,
  )
  return transformFullCreditApplicationFromApi(response.data)
}

export function useGetFullCreditApplication(
  id: string | undefined,
  financingProgramId: EFinancingProgram | undefined,
  shouldPoll = false,
): [
  FullCreditApplication | undefined,
  boolean,
  boolean,
  () => Promise<QueryObserverResult<FullCreditApplication, Error>>,
] {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: fullCreditApplicationKeys.detail(id ?? '', financingProgramId ?? EFinancingProgram.Auto),
    queryFn: () => getFullCreditApplication(id!, financingProgramId!),
    enabled: !!id && !!financingProgramId,
    refetchInterval: shouldPoll ? 5000 : undefined,
    staleTime: shouldPoll ? 0 : undefined,
  })
  return [data, isLoading, isError, refetch]
}

export function useInvalidateFullCreditApplication() {
  const queryClient = useQueryClient()

  return useCallback(
    async (creditApplicationId: string, financingProgramId: EFinancingProgram) => {
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId),
        }),
        queryClient.invalidateQueries({
          queryKey: availableCreditKeys.byApplicationId(financingProgramId, creditApplicationId),
        }),
      ])
    },
    [queryClient],
  )
}
